/**
 * Copyright (c) 2019 - 2023, all rights reserved.
 * State Of New Jersey Department Of Human Services
 * Division Of Family Development
 *
 * File: "shared.module.ts"
 * Project: "NJFLOURISH"
 * File Created: "date created"
 * Author: "PROTECH"
 * Description: "Shared Module for NJFLOURISH Application"
 *
 * History
 * -------
 * Last Modified: ""
 * Modified By: "PROTECH"
 * Update Comments: "reason for modification"
 * -----
 */

import { CommonModule, CurrencyPipe } from "@angular/common";
import { NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { CustomComponentModule } from "@mynjhelps/custom-component";
import { FormattersModule } from "@mynjhelps/formatters";
import {
  BreadcrumbModule,
  PanelMenuModule,
  PrimeNgModule
} from "@mynjhelps/primeng";
import { UibaseModule } from "@mynjhelps/uibase";
import { ValidationModule } from "@mynjhelps/validation";

import { AppInitService } from "../app-init.service";
import { OperatingHourGuard } from "./guard/operating-hour.guard";
import {
  AddressBlockComponent,
  ConfirmDialogComponent,
  PageContainerComponent,
  ScreenContainerComponent,
  UserInfoComponent
} from "./components/view";
import { LeftMenuComponent } from "./components/view/left-menu/left-menu.component";
import { AppConstants } from "./constants/app-constants";
import { MessageConstants } from "./constants/error-constants";
import { FieldSizeConstants } from "./constants/fieldSize-constants";
import { ScreenConstants } from "./constants/screen-constants";
import { BlockCopyPasteDirective } from "./directives/block-copy-paste.directive";
import { ControlSectionDirective } from "./directives/control-section.directive";
import { TooltipDirective } from "./directives/tooltip.directive";
import { AccessGuard } from "./guard/access.guard";
import { FaxPipe } from "./pipes/fax.pipe";
import { AddressPipe } from "./pipes/format-address.pipe";
import { FeinPipe } from "./pipes/format-fein.pipe";
import { NamePipe } from "./pipes/format-name.pipe";
import { FormatPhonePipe } from "./pipes/format-phone.pipe";
import { ReceiptPipe } from "./pipes/format-receipt.pipe";
import { PhonePipe } from "./pipes/phone.pipe";
import { RefmPipe } from "./pipes/refm-filler.pipe";
import { RelatedFieldPipe } from "./pipes/related-field.pipe";
import { SsnPipe } from "./pipes/ssn.pipe";
import { TitleCasePipe } from "./pipes/titleCase.pipe";
import { ZipPipe } from "./pipes/zip.pipe";
import { OptionPipe } from "./pipes/option.pipe";
import { DataStoreService } from "./services/data-store.service";
import { ScreenService } from "./services/screen.service";
import { StickyService } from "./services/sticky.service";
import { GalleriaModule } from "primeng/galleria";
import { TabMenuModule } from "primeng/tabmenu";
import { PopoverModule } from "primeng/popover";
import { DialogModule } from "primeng/dialog";
import { DateTimeZonePipe } from "./pipes/date-timezone.pipe";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { TranslatesPipe } from "./pipes/translate.pipe";
import { CaseLabelComponent } from "./components/view/case-label/case-label.component";
import { NgxCaptchaModule } from "ngx-captcha";
import { DatePickerModule } from "primeng/datepicker";
import { ScrollTopModule } from "primeng/scrolltop";
// import { AddressAutocompleteComponent } from "./components/view/address-autocomplete/address-autocomplete.component";
import { PanelMenuAriaFixDirective } from "./directives/panel-menu-aria-fix.directive";


@NgModule({
  declarations: [
    TooltipDirective,
    UserInfoComponent,
    RefmPipe,
    PhonePipe,
    ZipPipe,
    FaxPipe,
    TranslatesPipe,
    FeinPipe,
    TitleCasePipe,
    DateTimeZonePipe,
    ScreenContainerComponent,
    ConfirmDialogComponent,
    PageContainerComponent,
    //AddressAutocompleteComponent,
    AddressPipe,
    NamePipe,
    ReceiptPipe,
    FormatPhonePipe,
    RelatedFieldPipe,
    AddressBlockComponent,
    LeftMenuComponent,
    CaseLabelComponent,
    ControlSectionDirective,
    SsnPipe,
    OptionPipe,
    BlockCopyPasteDirective,
    PanelMenuAriaFixDirective
    
  ],
  imports: [
    NgxCaptchaModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PrimeNgModule,
    TabMenuModule,
    PopoverModule,
    DialogModule,
    DatePickerModule,
    ScrollTopModule,
    BreadcrumbModule,
    PanelMenuModule,
    CustomComponentModule,
    FormattersModule.forRoot(),
    ValidationModule,
    UibaseModule,
    GalleriaModule,
    TranslateModule.forChild({})
  ],
  exports: [
    TooltipDirective,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    PrimeNgModule,
    TabMenuModule,
    PopoverModule,
    DialogModule,
    DatePickerModule,
    ScrollTopModule,
    BreadcrumbModule,
    PanelMenuModule,
    CustomComponentModule,
    GalleriaModule,
    ScreenContainerComponent,
    RefmPipe,
    PhonePipe,
    ZipPipe,
    FeinPipe,
    FaxPipe,
    TranslatesPipe,
    DateTimeZonePipe,
    TitleCasePipe,
    ConfirmDialogComponent,
    PageContainerComponent,
    
    //AddressAutocompleteComponent,
    FormattersModule,
    ValidationModule,
    UibaseModule,
    AddressPipe,
    NamePipe,
    OptionPipe,
    ReceiptPipe,
    FormatPhonePipe,
    RelatedFieldPipe,
    AddressBlockComponent,
    CaseLabelComponent,
    LeftMenuComponent,
    ControlSectionDirective,
    UserInfoComponent,
    SsnPipe,
    BlockCopyPasteDirective,
    PanelMenuAriaFixDirective,
    TranslateModule,
    NgxCaptchaModule
  ],
  providers: [
    CurrencyPipe,
    RefmPipe,
    StickyService,
    FieldSizeConstants,
    ScreenConstants,
    MessageConstants,
    AppConstants,
    AccessGuard,
    ScreenService,
    AppInitService,
    DataStoreService,
    SsnPipe,
    NamePipe,
    OptionPipe,
    OperatingHourGuard
  ]
})
export class SharedModule {
  constructor(private translate: TranslateService) {}
}
